import React from 'react';
import { useTranslation } from 'next-i18next';
import { create, InstanceProps } from 'react-modal-promise';
import cn from 'classnames';

import { Icon, IconButton } from 'components';
import Modal from 'components/Modal';

import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import { FLAG_STATUS_CODES } from 'hooks/useCandidate';
import { TouchpointType } from 'lib/models/touchpoint';
import { useCommonList } from 'lib/contexts/common-list-context';

import styles from './ExternalApplySignup.module.scss';

interface ModalInstanceProps {
  touchpointable_type: TouchpointType;
}

const ModalInstance: React.FC<ModalInstanceProps & InstanceProps<number>> = ({
  isOpen,
  onResolve,
  onReject,
  touchpointable_type,
}) => {
  const [t] = useTranslation('opportunity');
  const {
    configData: { opportunity_count },
    isConfigDataLoading,
  } = useCommonList();

  const onSignInClickHandler = () => {
    onResolve(FLAG_STATUS_CODES.SIGN_IN_FLOW);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onReject(FLAG_STATUS_CODES.CANCEL)}
      contentClassName={styles.container}
    >
      <IconButton
        size="small"
        iconName="close"
        className={styles.cancelIcon}
        onClick={() => onReject(FLAG_STATUS_CODES.CANCEL)}
      />
      <p className={styles.title}>{t('external-apply-signup.title')}</p>
      <p className={styles.desc}>{t('external-apply-signup.desc')}</p>
      <section className={styles.features}>
        {!isConfigDataLoading ? (
          <Row
            wrap={false}
            align="center"
            columnGap={16}
            className={styles.featuresRow}
          >
            <Icon iconName="icon_briefcase" className={styles.featuresIcon} />
            <p className={styles.featuresText}>
              {t('external-apply-signup.features.0', { opportunity_count })}
            </p>
          </Row>
        ) : null}
        <Row
          wrap={false}
          align="center"
          columnGap={16}
          className={styles.featuresRow}
        >
          <Icon iconName="icon_magic-wand" className={styles.featuresIcon} />
          <p className={styles.featuresText}>
            {t('external-apply-signup.features.1')}
          </p>
        </Row>
        <Row
          wrap={false}
          align="center"
          columnGap={16}
          className={styles.featuresRow}
        >
          <Icon iconName="icon_document-2" className={styles.featuresIcon} />
          <p className={styles.featuresText}>
            {t('external-apply-signup.features.2')}
          </p>
        </Row>
        <Row
          wrap={false}
          align="center"
          columnGap={16}
          className={styles.featuresRow}
        >
          <Icon iconName="icon_bolt" className={styles.featuresIcon} />
          <p className={styles.featuresText}>
            {t('external-apply-signup.features.3')}
          </p>
        </Row>
        <Row
          wrap={false}
          align="center"
          columnGap={16}
          className={styles.featuresRow}
        >
          <Icon iconName="icon_medal" className={styles.featuresIcon} />
          <p className={styles.featuresText}>
            {t('external-apply-signup.features.4')}
          </p>
        </Row>
      </section>
      <Row
        isFullWidthRow
        columnGap={12}
        className={cn(styles.buttonsRow, styles.hideMobile)}
      >
        <ButtonV3
          size="large"
          className={styles.buttons}
          color="secondary"
          onClick={onSignInClickHandler}
        >
          {t('external-apply-signup.sign-in')}
        </ButtonV3>
        <ButtonV3
          size="large"
          className={styles.buttons}
          onClick={onSignInClickHandler}
        >
          {t('external-apply-signup.sign-up')}
        </ButtonV3>
      </Row>
      <section className={cn(styles.buttonsRow, styles.hideDesktop)}>
        <ButtonV3
          size="large"
          isFullWidth
          className={styles.buttons}
          onClick={onSignInClickHandler}
        >
          {t('external-apply-signup.sign-up')}
        </ButtonV3>
        <ButtonV3
          size="large"
          isFullWidth
          className={styles.buttons}
          color="secondary"
          onClick={onSignInClickHandler}
        >
          {t('external-apply-signup.sign-in')}
        </ButtonV3>
      </section>
      <Row
        align="center"
        justify="center"
        columnGap={4}
        className={styles['apply-anyways']}
      >
        <p className={styles['apply-anyways-text']}>
          {t('external-apply-signup.apply-anyways-text')}
        </p>
        <ButtonV3
          className={styles['apply-anyways-button']}
          variant="text"
          color="blue"
          onClick={() => onResolve(FLAG_STATUS_CODES.APPLY_ANYWAY)}
        >
          {touchpointable_type === TouchpointType.Event
            ? t('external-apply-signup.register-anyways')
            : t('external-apply-signup.apply-anyways')}
        </ButtonV3>
      </Row>
    </Modal>
  );
};

export const externalApplySignupModalInstance = create(ModalInstance);
